import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play, Pause, Volume2, VolumeX } from "lucide-react";

const NATIONAL_VIDEO =
  "https://customer-assets.emergentagent.com/job_elite-karate-3d/artifacts/r5qg48w3_1000028354.mp4";

const NATIONAL_POSTER =
  "https://customer-assets.emergentagent.com/job_elite-karate-3d/artifacts/a4f875vo_IMG_20260715_024326.webp";

const photos = [
  {
    src: "https://customer-assets.emergentagent.com/job_elite-karate-3d/artifacts/a4f875vo_IMG_20260715_024326.webp",
    label: "CAMPIONAT NAȚIONAL · 2025",
    caption: "Echipa Elite Center — Karate Shotokan E.S.K.U. Traditional",
    span: "md:col-span-7",
    aspect: "aspect-[4/5]",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_elite-karate-3d/artifacts/yj5w0cug_1000020279.webp",
    label: "CERTIFICĂRI · DOJO",
    caption: "Ceremonie de acordare — grade & centuri",
    span: "md:col-span-5",
    aspect: "aspect-[3/4]",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_elite-karate-3d/artifacts/nv5v2hz0_1000019920.webp",
    label: "CRĂCIUN · SALA",
    caption: "Copiii dojo-ului — moment de sărbătoare",
    span: "md:col-span-6",
    aspect: "aspect-[4/3]",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_elite-karate-3d/artifacts/2xt1rl0c_Screenshot_20260715_023934_com_instagram_android_TransparentModalActivity.jpg",
    label: "TATAMI · CONCURS",
    caption: "Sala de competiție — pregătirea pentru meciuri",
    span: "md:col-span-6",
    aspect: "aspect-[4/3]",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_elite-karate-3d/artifacts/867x4smm_1000019688.webp",
    label: "ARENA · COMPETIȚIE",
    caption: "Sală de sport — ediție de competiție",
    span: "md:col-span-12",
    aspect: "aspect-[21/9]",
  },
];

const rev = {
  hidden: { clipPath: "inset(100% 0% 0% 0%)" },
  show: {
    clipPath: "inset(0% 0% 0% 0%)",
    transition: { duration: 1.2, ease: [0.65, 0.05, 0.36, 1] },
  },
};

const FeaturedVideo = () => {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);

  const toggle = () => {
    const el = ref.current;
    if (!el) return;
    if (el.paused) {
      el.play();
      setPlaying(true);
    } else {
      el.pause();
      setPlaying(false);
    }
  };
  const toggleMute = (e) => {
    e.stopPropagation();
    const el = ref.current;
    if (!el) return;
    el.muted = !el.muted;
    setMuted(el.muted);
  };

  return (
    <motion.figure
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: "-80px" }}
      variants={rev}
      className="group mb-8 md:mb-12"
    >
      <div
        className="relative clip-frame aspect-[16/9] md:aspect-[21/9] border border-[rgba(242,235,229,0.15)] cursor-pointer bg-[#050505]"
        onClick={toggle}
        data-testid="featured-national-video"
      >
        <video
          ref={ref}
          src={NATIONAL_VIDEO}
          poster={NATIONAL_POSTER}
          playsInline
          muted={muted}
          loop
          preload="none"
          className="w-full h-full object-cover photo-editorial"
        />
        <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-[#050505]/70 via-transparent to-transparent" />
        <div className="absolute top-5 left-5 flex items-center gap-3">
          <span className="w-2 h-2 rounded-full bg-[#D91604] animate-pulse" />
          <span className="eyebrow text-[#f2ebe5]">Campionat Național · Karate Shotokan</span>
        </div>
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div
            className={`w-20 h-20 md:w-28 md:h-28 rounded-full border border-[rgba(242,235,229,0.7)] backdrop-blur-md bg-[#050505]/50 flex items-center justify-center transition-all duration-500 ${
              playing ? "opacity-0 scale-90 group-hover:opacity-100 group-hover:scale-100" : "opacity-100 scale-100"
            }`}
          >
            {playing ? (
              <Pause className="w-8 h-8 md:w-10 md:h-10 text-[#f2ebe5]" strokeWidth={1.5} />
            ) : (
              <Play className="w-8 h-8 md:w-10 md:h-10 text-[#f2ebe5] ml-1" strokeWidth={1.5} fill="#f2ebe5" />
            )}
          </div>
        </div>
        {playing && (
          <button
            onClick={toggleMute}
            className="absolute bottom-5 right-5 w-11 h-11 rounded-full border border-[rgba(242,235,229,0.4)] bg-[#050505]/60 backdrop-blur-md flex items-center justify-center hover:bg-[#D91604] hover:border-[#D91604] transition-colors"
            aria-label={muted ? "unmute" : "mute"}
          >
            {muted ? (
              <VolumeX className="w-4 h-4 text-[#f2ebe5]" strokeWidth={1.5} />
            ) : (
              <Volume2 className="w-4 h-4 text-[#f2ebe5]" strokeWidth={1.5} />
            )}
          </button>
        )}
        <div className="absolute bottom-5 left-5 font-jp text-xs tracking-widest text-[#f2ebe5]/70 bg-[#050505]/60 backdrop-blur px-3 py-1.5">
          REC · CAMPIONAT NAȚIONAL
        </div>
      </div>
      <figcaption className="flex items-center justify-between mt-4">
        <span className="text-xs md:text-sm text-[#8a8a8a] tracking-widest uppercase">
          Highlight — participarea Elite Center la Campionatul Național
        </span>
        <span className="text-xs text-[#8a8a8a]">/ FEATURED</span>
      </figcaption>
    </motion.figure>
  );
};

const PhotographyGallery = () => {
  return (
    <section
      id="galerie"
      className="section border-t border-[rgba(242,235,229,0.1)] bg-[#050505]"
      data-testid="gallery-section"
    >
      <div className="container-brutal">
        {/* Header */}
        <div className="grid grid-cols-12 gap-6 mb-16">
          <div className="col-span-12 md:col-span-3">
            <span className="eyebrow">§ 06 / Momente</span>
          </div>
          <div className="col-span-12 md:col-span-9 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9 }}
              className="section-title max-w-3xl"
            >
              Din sală<br />
              și de pe <span className="text-[#D91604]">podium.</span>
            </motion.h2>
            <p className="text-sm text-[#8a8a8a] max-w-xs">
              Momente reale de la antrenamente, sărbători și competiții — Campionatul Național 2025 și alte evenimente în dojo.
            </p>
          </div>
        </div>

        {/* Featured video */}
        <FeaturedVideo />

        {/* Photo grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8" data-testid="gallery-grid">
          {photos.map((img, i) => (
            <motion.figure
              key={i}
              className={`col-span-1 ${img.span} group`}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-80px" }}
              variants={rev}
            >
              <div className={`clip-frame ${img.aspect} border border-[rgba(242,235,229,0.15)]`}>
                <img
                  src={img.src}
                  alt={img.caption}
                  loading="lazy"
                  className="w-full h-full object-cover photo-editorial group-hover:scale-[1.03]"
                  data-testid={`gallery-image-${i}`}
                />
                <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-[#050505]/70 via-transparent to-transparent" />
                <div className="absolute top-4 left-4 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#D91604]" />
                  <span className="eyebrow text-[#f2ebe5]">{img.label}</span>
                </div>
              </div>
              <figcaption className="flex items-center justify-between mt-3">
                <span className="text-xs text-[#8a8a8a] tracking-widest uppercase">{img.caption}</span>
                <span className="text-xs text-[#8a8a8a]">/ 00{i + 2}</span>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PhotographyGallery;
